import { apiFetch } from "./apiFetch";
import { getApiErrorMessage, normalizeFetchError } from "./apiError";

const API_URL = process.env.REACT_APP_API_URL;

export type PurchaseMatchStatus = "NotMatched" | "Incomplete" | "Ready";

export type PurchaseMatchingCheck = {
  hasOpenOrders: boolean;
  hasOpenReceipts: boolean;
  openOrderLineCount: number;
  openReceiptLineCount: number;
  suggestGoodsInvoice: boolean;
};

export type OrderMatchCandidate = {
  entryNo: number;
  invoiceLineId: string;
  orderLineId: string;
  orderNo: string;
  orderLineNo: number;
  lineType: string;
  lineTypeText: string;
  no: string;
  description: string;
  unitOfMeasureCode: string;
  quantity: number;
  qtyRcdNotInvoiced: number;
  outstandingQuantity: number;
  qtyToInvoice: number;
  qtyToInvoiceBase: number;
  directUnitCost: number;
  locationCode: string;
  variantCode: string;
  currencyCode: string;
  expectedReceiptDate: string | null;
  orderDate: string | null;
  receiptOnInvoice: boolean;
  hasPostedReceipt: boolean;
  alreadyMatched: boolean;
  vendorOrderNo: string;
  yourReference: string;
};

/**
 * BC documents receiptMatchCandidates as a temporary buffer. Only identifiers
 * and fields used by the portal flow are required here; other BC fields remain
 * optional because the handover does not define a complete response schema.
 */
export type ReceiptMatchCandidate = {
  entryNo: number;
  invoiceLineId: string;
  receiptLineId: string;
  orderLineId?: string;
  receiptNo?: string;
  receiptLineNo?: number;
  orderNo?: string;
  orderLineNo?: number;
  lineType?: string;
  lineTypeText?: string;
  no?: string;
  description?: string;
  unitOfMeasureCode?: string;
  quantity?: number;
  qtyRcdNotInvoiced?: number;
  qtyToInvoice?: number;
  qtyToInvoiceBase?: number;
  directUnitCost?: number;
  locationCode?: string;
  variantCode?: string;
  currencyCode?: string;
  receiptOnInvoice?: boolean;
  hasPostedReceipt?: boolean;
  alreadyMatched: boolean;
};

export type MatchedLine = {
  entryNo: number;
  invoiceLineId: string;
  orderLineId: string;
  receiptLineId: string;
  orderNo: string;
  orderLineNo: number;
  receiptNo: string;
  receiptLineNo: number;
  lineType?: string;
  no?: string;
  description?: string;
  unitOfMeasureCode?: string;
  qtyToInvoice: number;
  qtyToInvoiceBase: number;
  receiptOnInvoice: boolean;
};

export type MatchSummary = {
  matched: boolean;
  orderLineCount: number;
  receiptLineCount: number;
  qtyToInvoice: number;
  lineQuantity: number;
  difference: number;
  hasReceiptMatch: boolean;
  ready: boolean;
  blockingReason: string;
};

export type PurchaseMatchingReadinessLine = MatchSummary & {
  invoiceLineId: string;
  lineNo: number | null;
};

export type PurchaseMatchingReadiness = {
  enabled: boolean;
  required: boolean;
  ready: boolean;
  lines: PurchaseMatchingReadinessLine[];
};

export type OrderDimensionValue = {
  slot: number;
  dimensionCode: string;
  dimensionValueCode: string;
};

export type OrderDimensionSource = "manual" | "template" | "order";

export type ApplyOrderDimensionsReason =
  | "applied"
  | "existingDimensions"
  | "matchingDisabled"
  | "lineDimensionsDisabled"
  | "notMatched"
  | "headerDimensions"
  | "dimensionSplit";

export type ApplyOrderDimensionsResult = {
  applied: boolean;
  needsConfirmation: boolean;
  reason: ApplyOrderDimensionsReason;
  currentSource?: OrderDimensionSource;
  current?: OrderDimensionValue[];
  proposed?: OrderDimensionValue[];
};

export type MatchingMutationResult = {
  message: string;
  matches: MatchedLine[];
  summary: MatchSummary;
};

function buildCompanyParams(companyId: string): URLSearchParams {
  const params = new URLSearchParams();
  params.set("companyId", companyId);
  return params;
}

function lineMatchingUrl(
  companyId: string,
  documentId: string,
  lineSystemId: string,
  action: string
): string {
  const params = buildCompanyParams(companyId);
  return `${API_URL}/documents/${documentId}/purchase-lines/${lineSystemId}/matching/${action}?${params.toString()}`;
}

async function parseApiResponse<T>(
  res: Response,
  fallbackMessage: string
): Promise<T> {
  if (!res.ok) {
    throw new Error(await getApiErrorMessage(res, fallbackMessage));
  }

  return res.json() as Promise<T>;
}

export async function checkPurchaseMatching(
  companyId: string,
  documentId: string
): Promise<PurchaseMatchingCheck> {
  try {
    const params = buildCompanyParams(companyId);
    const res = await apiFetch(
      `${API_URL}/documents/${documentId}/purchase-matching/check?${params.toString()}`,
      { method: "POST" }
    );

    return await parseApiResponse<PurchaseMatchingCheck>(
      res,
      "Nie udało się sprawdzić zamówień i przyjęć dla faktury."
    );
  } catch (e) {
    throw normalizeFetchError(
      e,
      "Nie udało się sprawdzić zamówień i przyjęć dla faktury."
    );
  }
}

export async function updateGoodsInvoice(
  companyId: string,
  documentId: string,
  goodsInvoice: boolean
): Promise<{ goodsInvoice: boolean }> {
  try {
    const params = buildCompanyParams(companyId);
    const res = await apiFetch(
      `${API_URL}/documents/${documentId}/purchase-matching/goods-invoice?${params.toString()}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ goodsInvoice }),
      }
    );

    return await parseApiResponse<{ goodsInvoice: boolean }>(
      res,
      "Nie udało się zaktualizować typu faktury."
    );
  } catch (e) {
    throw normalizeFetchError(e, "Nie udało się zaktualizować typu faktury.");
  }
}

export async function getDocumentMatches(
  companyId: string,
  documentId: string
): Promise<MatchedLine[]> {
  const params = buildCompanyParams(companyId);
  const res = await apiFetch(
    `${API_URL}/documents/${documentId}/purchase-matching/matches?${params.toString()}`
  );
  const payload = await parseApiResponse<{ items: MatchedLine[] }>(
    res,
    "Nie udało się pobrać dopasowań faktury."
  );
  return Array.isArray(payload.items) ? payload.items : [];
}

export async function getPurchaseMatchingReadiness(
  companyId: string,
  documentId: string
): Promise<PurchaseMatchingReadiness> {
  try {
    const params = buildCompanyParams(companyId);
    const res = await apiFetch(
      `${API_URL}/documents/${documentId}/purchase-matching/readiness?${params.toString()}`
    );

    return await parseApiResponse<PurchaseMatchingReadiness>(
      res,
      "Nie udało się sprawdzić gotowości dopasowania ZZ/PZ."
    );
  } catch (e) {
    throw normalizeFetchError(
      e,
      "Nie udało się sprawdzić gotowości dopasowania ZZ/PZ."
    );
  }
}

export async function getOrderMatchCandidates(
  companyId: string,
  documentId: string,
  lineSystemId: string
): Promise<OrderMatchCandidate[]> {
  const res = await apiFetch(
    lineMatchingUrl(companyId, documentId, lineSystemId, "order-candidates")
  );
  const payload = await parseApiResponse<{ items: OrderMatchCandidate[] }>(
    res,
    "Nie udało się pobrać kandydatów zamówień."
  );
  return Array.isArray(payload.items) ? payload.items : [];
}

export async function getReceiptMatchCandidates(
  companyId: string,
  documentId: string,
  lineSystemId: string,
  orderLineId?: string
): Promise<ReceiptMatchCandidate[]> {
  const params = buildCompanyParams(companyId);
  if (orderLineId) {
    params.set("orderLineId", orderLineId);
  }

  const res = await apiFetch(
    `${API_URL}/documents/${documentId}/purchase-lines/${lineSystemId}/matching/receipt-candidates?${params.toString()}`
  );
  const payload = await parseApiResponse<{ items: ReceiptMatchCandidate[] }>(
    res,
    "Nie udało się pobrać kandydatów przyjęć."
  );
  return Array.isArray(payload.items) ? payload.items : [];
}

export async function getLineMatches(
  companyId: string,
  documentId: string,
  lineSystemId: string
): Promise<MatchedLine[]> {
  const res = await apiFetch(
    lineMatchingUrl(companyId, documentId, lineSystemId, "matches")
  );
  const payload = await parseApiResponse<{ items: MatchedLine[] }>(
    res,
    "Nie udało się pobrać dopasowań wiersza faktury."
  );
  return Array.isArray(payload.items) ? payload.items : [];
}

export async function matchOrderLines(
  companyId: string,
  documentId: string,
  lineSystemId: string,
  orderLineIds: string[]
): Promise<MatchingMutationResult> {
  try {
    const res = await apiFetch(
      lineMatchingUrl(companyId, documentId, lineSystemId, "orders"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderLineIds }),
      }
    );
    return await parseApiResponse<MatchingMutationResult>(
      res,
      "Nie udało się dopasować zamówień do wiersza faktury."
    );
  } catch (e) {
    throw normalizeFetchError(
      e,
      "Nie udało się dopasować zamówień do wiersza faktury."
    );
  }
}

export async function matchReceiptLines(
  companyId: string,
  documentId: string,
  lineSystemId: string,
  receiptLineIds: string[]
): Promise<MatchingMutationResult> {
  try {
    const res = await apiFetch(
      lineMatchingUrl(companyId, documentId, lineSystemId, "receipts"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ receiptLineIds }),
      }
    );
    return await parseApiResponse<MatchingMutationResult>(
      res,
      "Nie udało się dopasować przyjęć do wiersza faktury."
    );
  } catch (e) {
    throw normalizeFetchError(
      e,
      "Nie udało się dopasować przyjęć do wiersza faktury."
    );
  }
}

export async function removeMatch(
  companyId: string,
  documentId: string,
  lineSystemId: string,
  orderLineId: string,
  receiptLineId?: string
): Promise<MatchingMutationResult> {
  try {
    const res = await apiFetch(
      lineMatchingUrl(companyId, documentId, lineSystemId, "remove"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderLineId,
          receiptLineId: receiptLineId || null,
        }),
      }
    );
    return await parseApiResponse<MatchingMutationResult>(
      res,
      "Nie udało się usunąć dopasowania."
    );
  } catch (e) {
    throw normalizeFetchError(e, "Nie udało się usunąć dopasowania.");
  }
}

export async function clearMatches(
  companyId: string,
  documentId: string,
  lineSystemId: string
): Promise<MatchingMutationResult> {
  try {
    const res = await apiFetch(
      lineMatchingUrl(companyId, documentId, lineSystemId, "clear"),
      { method: "POST" }
    );
    return await parseApiResponse<MatchingMutationResult>(
      res,
      "Nie udało się usunąć dopasowań wiersza faktury."
    );
  } catch (e) {
    throw normalizeFetchError(
      e,
      "Nie udało się usunąć dopasowań wiersza faktury."
    );
  }
}

export async function applyOrderDimensions(
  companyId: string,
  documentId: string,
  lineSystemId: string,
  force: boolean
): Promise<ApplyOrderDimensionsResult> {
  try {
    const res = await apiFetch(
      lineMatchingUrl(companyId, documentId, lineSystemId, "apply-dimensions"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ force }),
      }
    );
    return await parseApiResponse<ApplyOrderDimensionsResult>(
      res,
      "Nie udało się zastosować wymiarów z zamówienia."
    );
  } catch (e) {
    throw normalizeFetchError(
      e,
      "Nie udało się zastosować wymiarów z zamówienia."
    );
  }
}

export async function setMatchQuantity(
  companyId: string,
  documentId: string,
  lineSystemId: string,
  orderLineId: string,
  qtyToInvoice: number,
  receiptLineId?: string
): Promise<MatchingMutationResult> {
  try {
    const res = await apiFetch(
      lineMatchingUrl(companyId, documentId, lineSystemId, "quantity"),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          orderLineId,
          receiptLineId: receiptLineId || null,
          qtyToInvoice,
        }),
      }
    );
    return await parseApiResponse<MatchingMutationResult>(
      res,
      "Nie udało się zmienić ilości dopasowania."
    );
  } catch (e) {
    throw normalizeFetchError(e, "Nie udało się zmienić ilości dopasowania.");
  }
}

export async function getMatchSummary(
  companyId: string,
  documentId: string,
  lineSystemId: string
): Promise<MatchSummary> {
  try {
    const res = await apiFetch(
      lineMatchingUrl(companyId, documentId, lineSystemId, "summary"),
      { method: "POST" }
    );
    return await parseApiResponse<MatchSummary>(
      res,
      "Nie udało się pobrać podsumowania dopasowania."
    );
  } catch (e) {
    throw normalizeFetchError(
      e,
      "Nie udało się pobrać podsumowania dopasowania."
    );
  }
}
